<template>
  <section class="data-section-header">
    <InfoLabel :title="title" :info="info" :size="'mediumBold'" :fullscreen="fullscreen" />
    <ToggleMenu
      :selected="selectedToggles"
      :enabledButtons="enabledToggleButtons"
      :link="link"
      @toggle="changeCards"
    />
  </section>
  <div class="cover-container" :class="{ covering: dataState !== 'loaded' }">
    <div v-if="dataState !== 'loaded'" class="cover">
      <template v-if="dataState === 'unsupported'">
        <DashboardMissingFilter />
      </template>
      <template v-else-if="dataState === 'loading'">
        <DashboardLoading />
      </template>
      <template v-else-if="dataState === 'empty'">
        <DashboardMissingData />
      </template>
      <template v-else-if="dataState === 'error'">
        <DashboardError />
      </template>
    </div>
    <section class="data-section-content">
      <FeatureChildren
        feature="home/*/*/*/data/*"
        :filter="
          (f) =>
            f.type === undefined || (f.type !== undefined && componentTypes.indexOf(f.type) !== -1)
        "
      />
    </section>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, toRefs, watch, ref, onMounted } from 'vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import { sharedDataKey } from '../../types'
import DashboardMissingFilter from '../components/DashboardMissingFilter.vue'
import DashboardLoading from './DashboardLoading.vue'
import DashboardMissingData from './DashboardMissingData.vue'
import { EntityLevel } from '@/composables/useEntityLevel'
import { storeToRefs } from 'pinia'
import FeatureChildren from '@/components/FeatureChildren.vue'
import { DashboardComponentType, MeasurementSource } from '@/features/dashboard'
import { useMeasurements } from '../measurements/useMeasurements'
import ToggleMenu from '@/design-system/molecules/ToggleMenu.vue'
import { Feature } from '@/pinia/feature'
import { useRouter, useRoute } from 'vue-router'
import DashboardError from './DashboardError.vue'

type Props = {
  title: string
  info?: string
  link?: { address: string; name: string }
  enabledEntityLevels?: EntityLevel[]
  measurementSources: MeasurementSource[]
  children?: Feature[]
}

const componentTypes = ref<string[]>(['kpi'])

const props = withDefaults(defineProps<Props>(), {
  enabledEntityLevels: () => [
    'REGION',
    'COMPLEX',
    'REAL_ESTATE',
    'CONSTRUCTION',
    'LEVEL',
    'FLOOR',
    'SPACE'
  ],
  info: undefined,
  link: undefined,
  children: () => []
})

const { measurementSources, children } = toRefs(props)

const { setMeasurements } = useMeasurements()
const router = useRouter()
const route = useRoute()
const selectedToggles = ref<string[]>([(route.query.components as string) || 'measurement'])
watch(
  () => route.query.toggle,
  (newView) => {
    selectedToggles.value = [typeof newView === 'string' ? newView : 'measurement']
  }
)
watch(
  measurementSources,
  () => {
    setMeasurements(measurementSources.value)
  },
  { immediate: true }
)

const { dataState } = storeToRefs(useMeasurements())

const existingComponentTypes = children.value
  .map((c) => c.type)
  .filter((c) => c !== undefined) as DashboardComponentType[]

const enabledToggleButtons = computed(() => {
  const buttons = ['all']
  if (existingComponentTypes.includes(DashboardComponentType.Kpi)) {
    buttons.push('measurement')
  }
  if (
    existingComponentTypes.includes(DashboardComponentType.TimeSeries) ||
    existingComponentTypes.includes(DashboardComponentType.Toplist) ||
    existingComponentTypes.includes(DashboardComponentType.Histogram)
  ) {
    buttons.push('timeseries')
  }
  if (existingComponentTypes.includes(DashboardComponentType.Heatmap)) {
    buttons.push('heatmap')
  }
  return buttons
})

const sharedData = inject(sharedDataKey)

const fullscreen = computed<boolean>(() => sharedData?.fullscreen ?? false)

function changeCards(selection: string) {
  selectedToggles.value = [selection]
  switch (selection) {
    case 'measurement':
      componentTypes.value = ['kpi']
      break
    case 'graphs':
      componentTypes.value = ['timeseries', 'toplist', 'histogram', 'barchart']
      break
    case 'heatmap':
      componentTypes.value = ['heatmap']
      break
    case 'all':
      componentTypes.value = ['kpi', 'timeseries', 'toplist', 'histogram', 'heatmap', 'barchart']
      break
  }

  router.push({ query: { ...route.query, components: selection } })
}
onMounted(() => {
  changeCards(selectedToggles.value[0])
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.data-section-header {
  display: flex;
  padding: 2px;
  margin-top: 16px;
  margin-bottom: 16px;
  align-content: flex-start;
  align-items: center;
  justify-content: space-between;
}
.data-section-content {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  width: 100%;
}

.title-row {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-greyscale-content;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 4px;
}

.legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
}

.cover-container {
  position: relative;
  background-color: inherit;
  height: fit-content;
  padding: 0 3px 3px 3px;

  &.covering {
    height: 200px;
    overflow: hidden;
  }
}

.cover {
  position: absolute;
  background-color: inherit;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  z-index: 1;
  display: flex;
  justify-content: center;
}
</style>
